<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useEvaluationFileModelStore} from "@/stores/Evaluation/EvaluationFileModel";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
        type: String,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);

        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {evaluationCycles} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();

        const evaluationFileModelStore = useEvaluationFileModelStore();

        const evaluationFileModel = ref({
            steps: [{}],
            occupationIds: [],
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationFileModels/${id}`).then((response) => {
                evaluationFileModel.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationFileModel.value.type = props.type;
                buildOrder();
                axios.post('/evaluationFileModels', evaluationFileModel.value).then((response) => {
                    evaluationFileModelStore.addEvaluationFileModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationFileModel.value.type = props.type;
                buildOrder();
                axios.put('/evaluationFileModels', evaluationFileModel.value).then((response) => {
                    evaluationFileModelStore.updateEvaluationFileModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function buildOrder() {
            evaluationFileModel.value.steps.forEach((step, index) => {
                step.order = index;
            });
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/evaluationFileModels/${evaluationFileModel.value.id}`).then(() => {
                    evaluationFileModelStore.deleteEvaluationFileModel(evaluationFileModel.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function addStep() {
            evaluationFileModel.value.steps.push({});
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            evaluationFileModelStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            evaluationFileModel,
            evaluationCycles,
            addStep,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="800"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.general')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="evaluationFileModel.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('evaluation.cycle')" required/>
                                    <w-enum-select
                                        v-model="evaluationFileModel.cycle"
                                        :items="evaluationCycles"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('module.occupations')" required/>
                                    <str-occupation-autocomplete
                                        v-model="evaluationFileModel.occupationIds"
                                        :readonly="readonly"
                                        multiple
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset
                            :title="$t('common.steps')"
                            :show-add-action="!readonly"
                            @add="addStep"
                        >
                            <v-row
                                v-for="(step, index) in evaluationFileModel.steps"
                                :key="`step-${index}`"
                                dense
                            >
                                <v-col cols="11">
                                    <w-label :label="$t('common.step')" required/>
                                    <str-evaluation-phase-autocomplete
                                        v-model="step.phaseId"
                                        :type="type"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="1">
                                    <w-btn-icon
                                        color="red"
                                        classe="mt-6"
                                        icon="mdi-trash-can"
                                        :disabled="readonly || evaluationFileModel.steps.length === 1"
                                        @press="evaluationFileModel.steps.splice(index, 1)"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>